import type {
  AtendimentoDoCliente,
  ClienteAtual,
  ItemHistorico,
  RespostaEnvioCodigo,
  RespostaIdentificacao,
  RespostaValidacaoCodigo,
  SessaoCliente,
  SolicitacaoTitularResumo,
} from '@mymaxx/shared';
import { lerAccessToken, useSessao } from './sessao';

const BASE_URL = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '') ?? '';

export class ErroApi extends Error {
  constructor(
    public readonly status: number,
    public readonly corpo: {
      message?: string;
      erros?: { campo: string; mensagem: string }[];
    } | null,
  ) {
    super(corpo?.message ?? `Erro ${status}`);
  }
  get mensagem(): string {
    if (this.corpo?.erros?.length) return this.corpo.erros.map((e) => e.mensagem).join('. ');
    if (typeof this.corpo?.message === 'string') return this.corpo.message;
    return this.status === 0
      ? 'Não foi possível falar com o servidor. Verifique sua conexão.'
      : `Erro ${this.status}`;
  }
}

interface Opcoes extends Omit<RequestInit, 'body'> {
  body?: unknown;
  autenticado?: boolean;
}

let refreshEmAndamento: Promise<boolean> | null = null;

export async function tentarRefresh(): Promise<boolean> {
  refreshEmAndamento ??= (async () => {
    try {
      const r = await fetch(`${BASE_URL}/v1/auth/cliente/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!r.ok) return false;
      useSessao.getState().definir((await r.json()) as SessaoCliente);
      return true;
    } catch {
      return false;
    } finally {
      refreshEmAndamento = null;
    }
  })();
  return refreshEmAndamento;
}

async function executar<T>(caminho: string, opcoes: Opcoes, tentarNovamente: boolean): Promise<T> {
  const { body, autenticado = true, headers, ...init } = opcoes;
  const token = autenticado ? lerAccessToken() : null;
  let resposta: Response;
  try {
    resposta = await fetch(`${BASE_URL}${caminho}`, {
      ...init,
      credentials: 'include',
      headers: {
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(headers ?? {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new ErroApi(0, null);
  }

  if (resposta.status === 401 && autenticado && tentarNovamente && (await tentarRefresh())) {
    return executar<T>(caminho, opcoes, false);
  }
  if (resposta.status === 401 && autenticado) useSessao.getState().limpar();

  const corpo = resposta.status === 204 ? null : await resposta.json().catch(() => null);
  if (!resposta.ok) throw new ErroApi(resposta.status, corpo);
  return corpo as T;
}

const publico = { autenticado: false } as const;

export const api = {
  get: <T>(caminho: string, opcoes: Opcoes = {}) =>
    executar<T>(caminho, { ...opcoes, method: 'GET' }, true),
  post: <T>(caminho: string, body?: unknown, opcoes: Opcoes = {}) =>
    executar<T>(caminho, { ...opcoes, method: 'POST', body }, true),
  patch: <T>(caminho: string, body?: unknown, opcoes: Opcoes = {}) =>
    executar<T>(caminho, { ...opcoes, method: 'PATCH', body }, true),
  delete: <T>(caminho: string, opcoes: Opcoes = {}) =>
    executar<T>(caminho, { ...opcoes, method: 'DELETE' }, true),
};

export const authApi = {
  identificar: (documento: string) =>
    api.post<RespostaIdentificacao>('/v1/auth/cliente/identificar', { documento }, publico),
  enviarCodigo: (tokenIdentificacao: string, contatoId: string) =>
    api.post<RespostaEnvioCodigo>(
      '/v1/auth/cliente/codigo/enviar',
      { tokenIdentificacao, contatoId },
      publico,
    ),
  validarCodigo: (tokenIdentificacao: string, codigo: string) =>
    api.post<RespostaValidacaoCodigo>(
      '/v1/auth/cliente/codigo/validar',
      { tokenIdentificacao, codigo },
      publico,
    ),
  criarConta: (tokenVerificado: string, senha: string) =>
    api.post<SessaoCliente>(
      '/v1/auth/cliente/conta',
      { tokenVerificado, senha, aceitePrivacidade: true },
      publico,
    ),
  entrarComCodigo: (tokenVerificado: string) =>
    api.post<SessaoCliente>('/v1/auth/cliente/entrar-com-codigo', { tokenVerificado }, publico),
  redefinirSenha: (tokenVerificado: string, novaSenha: string) =>
    api.post<SessaoCliente>(
      '/v1/auth/cliente/senha/redefinir',
      { tokenVerificado, novaSenha },
      publico,
    ),
  login: (documento: string, senha: string) =>
    api.post<SessaoCliente>('/v1/auth/cliente/login', { documento, senha }, publico),
  trocarSenha: (senhaAtual: string, novaSenha: string) =>
    api.post<void>('/v1/auth/cliente/senha/trocar', { senhaAtual, novaSenha }),

  /**
   * Foto de perfil do técnico, em dois passos como qualquer anexo: pede a URL, manda o
   * arquivo direto ao storage e confirma.
   */
  async trocarFoto(arquivo: File): Promise<ClienteAtual> {
    const destino = await api.post<{ chave: string; url: string }>(
      '/v1/auth/cliente/avatar/preparar',
      { mimeType: arquivo.type, tamanho: arquivo.size },
    );
    const envio = await fetch(destino.url, {
      method: 'PUT',
      body: arquivo,
      headers: { 'Content-Type': arquivo.type },
    });
    if (!envio.ok) throw new Error('Não consegui enviar a foto. Tente de novo.');
    return api.post<ClienteAtual>('/v1/auth/cliente/avatar', { chave: destino.chave });
  },
  removerFoto: () => api.delete<ClienteAtual>('/v1/auth/cliente/avatar'),
  logout: () => api.post<void>('/v1/auth/cliente/logout', undefined, publico),
  eu: () => api.get<ClienteAtual>('/v1/auth/cliente/eu'),
};

/** Exportação "meus dados": PDF (leitura) ou JSON (portabilidade). */
export type FormatoExportacao = 'pdf' | 'json';

/** Histórico extenso: a API responde 202 e o arquivo chega por link. */
export interface ExportacaoAgendada {
  agendada: true;
  mensagem: string;
  destino: string | null;
}

export type ResultadoExportacao = { agendada: false } | ExportacaoAgendada;

export const clienteApi = {
  historico: () => api.get<ItemHistorico[]>('/v1/cliente/historico'),

  /** Os setores abertos ao técnico. Vazio para cliente comum: a triagem é que escolhe. */
  setoresDoTecnico: () =>
    api.get<{ id: string; nome: string; descricao: string | null }[]>('/v1/chat/setores-tecnico'),

  /**
   * Baixa o arquivo direto no navegador. Não usa o helper `api` porque a resposta é binária;
   * em 401 tenta um refresh e repete, como nas demais chamadas.
   */
  async exportarDados(formato: FormatoExportacao = 'pdf'): Promise<ResultadoExportacao> {
    const baixar = async (): Promise<Response> =>
      fetch(`${BASE_URL}/v1/cliente/dados?formato=${formato}`, {
        credentials: 'include',
        headers: { Authorization: `Bearer ${lerAccessToken() ?? ''}` },
      });

    let resposta = await baixar();
    if (resposta.status === 401 && (await tentarRefresh())) resposta = await baixar();
    if (resposta.status === 202) return (await resposta.json()) as ExportacaoAgendada;
    if (!resposta.ok) throw new ErroApi(resposta.status, await resposta.json().catch(() => null));

    const blob = await resposta.blob();
    const nome =
      /filename="([^"]+)"/.exec(resposta.headers.get('content-disposition') ?? '')?.[1] ??
      `mymaxx-meus-dados.${formato}`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = nome;
    a.click();
    URL.revokeObjectURL(url);
    return { agendada: false };
  },
  /** Um atendimento anterior, para ler dentro da conta (só leitura). */
  atendimento: (id: string) => api.get<AtendimentoDoCliente>(`/v1/cliente/atendimentos/${id}`),

  /** O PDF daquele protocolo. Binário e autenticado: precisa de fetch com Bearer. */
  async baixarAtendimento(id: string, protocolo: string): Promise<void> {
    const buscar = () =>
      fetch(`${BASE_URL}/v1/cliente/atendimentos/${id}/transcricao.pdf`, {
        credentials: 'include',
        headers: { Authorization: `Bearer ${lerAccessToken() ?? ''}` },
      });
    let resposta = await buscar();
    if (resposta.status === 401 && (await tentarRefresh())) resposta = await buscar();
    if (!resposta.ok) throw new ErroApi(resposta.status, await resposta.json().catch(() => null));

    const url = URL.createObjectURL(await resposta.blob());
    const a = document.createElement('a');
    a.href = url;
    a.download = `atendimento-${protocolo.replace(/[^d]/g, '')}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  },

  solicitacoes: () => api.get<SolicitacaoTitularResumo[]>('/v1/cliente/solicitacoes'),
  solicitarExclusao: (motivo?: string) =>
    api.post<SolicitacaoTitularResumo>('/v1/cliente/solicitacoes/exclusao', { motivo }),
};
